// キーの割り当て (2026-10-31 作者「ブラウザ版に先に入れる」「コントローラーも対応して」)。
//   戦闘中の操作だけを変えられる。 メニューと物語画面の操作 (左クリック=決定・Space=文章送り・Esc=戻る /
//   パッド ✕=決定・○=戻る) は変えない = どう割り当てても、 メニューから戻れなくなる事が無い。
//   読むのは input.ts (戦闘中の入力) と input_labels.ts (画面の表記)。 保存は settings とは別のキー。

export type KbmBindAction = 'left' | 'right' | 'attack' | 'parry' | 'step'
export type PadBindAction = 'attack' | 'parry' | 'step'

export type KeyBindings = {
  // KeyboardEvent.code、 またはマウスのボタン "Mouse<button>"。
  kbm: Record<KbmBindAction, string>
  // Gamepad の標準マッピングのボタン番号。
  pad: Record<PadBindAction, number>
}

export const KBM_BIND_ACTIONS: readonly KbmBindAction[] = ['left', 'right', 'attack', 'parry', 'step']
export const PAD_BIND_ACTIONS: readonly PadBindAction[] = ['attack', 'parry', 'step']

// 既定 = 今までの固定の割り当てと同じ (input.ts の旧 keyToAction / PAD_BUTTON_TO_ACTION)。
export const DEFAULT_KEY_BINDINGS: KeyBindings = {
  kbm: { left: 'KeyA', right: 'KeyD', attack: 'Mouse0', parry: 'Space', step: 'Mouse2' },
  pad: { attack: 5, parry: 4, step: 1 },
}

// 割り当てに使えないもの。 Esc = 幕間と「戻る」専用。 パッドの OPTIONS(9) = 幕間・十字(12-15) = 移動・ホーム(16) = OS が使う。
//   ✕(0) は割り当ててよい (戦闘中は再演に使わない・メニューでは既定のまま決定)。
export const RESERVED_KBM_CODES: ReadonlySet<string> = new Set(['Escape'])
export const RESERVED_PAD_BUTTONS: ReadonlySet<number> = new Set([9, 12, 13, 14, 15, 16])

const STORAGE_KEY = 'shinige-dreamland:keys:v1'

export function cloneBindings(b: KeyBindings): KeyBindings {
  return { kbm: { ...b.kbm }, pad: { ...b.pad } }
}

export function isAssignableKbmCode(code: string): boolean {
  if (RESERVED_KBM_CODES.has(code)) return false
  if (/^Mouse[0-4]$/.test(code)) return true
  // KeyboardEvent.code の形 (英字始まりの英数字)。 空や記号だけの物は弾く。
  return /^[A-Za-z][A-Za-z0-9]*$/.test(code)
}

export function isAssignablePadButton(button: number): boolean {
  return Number.isInteger(button) && button >= 0 && button <= 15 && !RESERVED_PAD_BUTTONS.has(button)
}

// 割り当て (純関数)。 他の所作が同じキーを使っていたら、 そちらへ今のキーを渡す (入れ替え)。
//   = 1つのキーに2つの所作が載らない・どの所作もキーを失わない。
export function assignKbm(b: KeyBindings, action: KbmBindAction, code: string): KeyBindings {
  if (!isAssignableKbmCode(code)) return b
  const next = cloneBindings(b)
  const prev = next.kbm[action]
  for (const other of KBM_BIND_ACTIONS) {
    if (other !== action && next.kbm[other] === code) next.kbm[other] = prev
  }
  next.kbm[action] = code
  return next
}

export function assignPad(b: KeyBindings, action: PadBindAction, button: number): KeyBindings {
  if (!isAssignablePadButton(button)) return b
  const next = cloneBindings(b)
  const prev = next.pad[action]
  for (const other of PAD_BIND_ACTIONS) {
    if (other !== action && next.pad[other] === button) next.pad[other] = prev
  }
  next.pad[action] = button
  return next
}

// 保存の読み直し。 壊れていたら、 その所作だけ既定へ戻す。 重なりが残ったら全体を既定へ (安全側)。
export function sanitizeBindings(raw: unknown): KeyBindings {
  const out = cloneBindings(DEFAULT_KEY_BINDINGS)
  if (typeof raw !== 'object' || raw === null) return out
  const r = raw as { kbm?: Record<string, unknown>, pad?: Record<string, unknown> }
  for (const a of KBM_BIND_ACTIONS) {
    const v = r.kbm?.[a]
    if (typeof v === 'string' && isAssignableKbmCode(v)) out.kbm[a] = v
  }
  for (const a of PAD_BIND_ACTIONS) {
    const v = r.pad?.[a]
    if (typeof v === 'number' && isAssignablePadButton(v)) out.pad[a] = v
  }
  if (new Set(Object.values(out.kbm)).size !== KBM_BIND_ACTIONS.length) out.kbm = { ...DEFAULT_KEY_BINDINGS.kbm }
  if (new Set(Object.values(out.pad)).size !== PAD_BIND_ACTIONS.length) out.pad = { ...DEFAULT_KEY_BINDINGS.pad }
  return out
}

export function loadKeyBindings(): KeyBindings {
  try {
    if (typeof localStorage === 'undefined') return cloneBindings(DEFAULT_KEY_BINDINGS)
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? sanitizeBindings(JSON.parse(raw)) : cloneBindings(DEFAULT_KEY_BINDINGS)
  } catch {
    return cloneBindings(DEFAULT_KEY_BINDINGS)
  }
}

export function saveKeyBindings(b: KeyBindings): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, JSON.stringify(b))
  } catch {}
}

// ===== 画面の表記 =====
const KBM_NAMES: Record<string, string> = {
  Mouse0: '左クリック', Mouse1: '中クリック', Mouse2: '右クリック', Mouse3: 'マウス戻る', Mouse4: 'マウス進む',
  Space: 'Space', Enter: 'Enter', Tab: 'Tab', Backspace: 'Backspace', CapsLock: 'CapsLock',
  ShiftLeft: '左Shift', ShiftRight: '右Shift', ControlLeft: '左Ctrl', ControlRight: '右Ctrl',
  AltLeft: '左Alt', AltRight: '右Alt', MetaLeft: '左⌘', MetaRight: '右⌘',
  ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓',
  Semicolon: ';', Quote: "'", Comma: ',', Period: '.', Slash: '/', Backslash: '\\',
  BracketLeft: '[', BracketRight: ']', Minus: '-', Equal: '=', Backquote: '`',
  IntlRo: 'ろ', IntlYen: '¥', Lang1: 'かな', Lang2: '英数', Convert: '変換', NonConvert: '無変換',
}

export function kbmCodeLabel(code: string): string {
  const named = KBM_NAMES[code]
  if (named) return named
  if (/^Key[A-Z]$/.test(code)) return code.slice(3)
  if (/^Digit[0-9]$/.test(code)) return code.slice(5)
  if (/^Numpad[0-9]$/.test(code)) return `テンキー${code.slice(6)}`
  if (/^F[0-9]{1,2}$/.test(code)) return code
  return code
}

const PAD_NAMES: Record<'ps' | 'xbox', string[]> = {
  ps: ['✕', '○', '□', '△', 'L1', 'R1', 'L2', 'R2', 'CREATE', 'OPTIONS', 'L3', 'R3', '十字↑', '十字↓', '十字←', '十字→'],
  xbox: ['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'VIEW', 'MENU', 'LS', 'RS', '十字↑', '十字↓', '十字←', '十字→'],
}

export function padButtonLabel(button: number, glyph: 'ps' | 'xbox'): string {
  return PAD_NAMES[glyph][button] ?? `ボタン${button}`
}
